'use client';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { removeProduct, setVariantWithQuantity } from '@shared/components/cart/actions';
import BDImage from '@shared/components/elements/bd-image';
import BDButton from '@shared/components/elements/button';
import BDDialog from '@shared/components/elements/dialog';
import { HorizontalLine } from '@shared/components/elements/lines';
import BDTitle from '@shared/components/elements/title';
import { HSection } from '@shared/components/home/section';
import { getLinesByProductId } from '@shared/components/order/cart-manager';
import { CHECKOUT_CART_COOKIE_NAME } from '@shared/lib/constants';
import { Product, ProductVariant, Image as ShopifyImage } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import { withAddToCartTracking } from '@shared/lib/utils/conversion-tracking';
import { renderMoney, renderMoneyRange } from '@shared/lib/utils/money';
import Image from 'next/image';
import Link from 'next/link';
import { useActionState, useMemo, useState } from 'react';
import ImageCarousel from '../jewelry/carousel';

type ShopServiceProps = {
  cart: Cart | undefined;
  kitProduct: Product;
  premiumProcessingProduct: Product;
  clubPlatinumVariant: ProductVariant;
};
export default function ShopService({
  cart,
  kitProduct,
  premiumProcessingProduct,
  clubPlatinumVariant,
}: ShopServiceProps) {
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const [state, formAction, pending] = useActionState(
    setVariantWithQuantity.bind(null, {
      cartId: cart?.id,
      cartCookieName: CHECKOUT_CART_COOKIE_NAME,
      variantId: kitProduct.variants[0]!.id,
      quantity: 1,
    }),
    null
  );

  // Create a wrapped version of the form action
  const trackedFormAction = useMemo(
    () => withAddToCartTracking(formAction, [kitProduct.variants[0]!], 1),
    [formAction, kitProduct.variants]
  );

  const kitInCart = cart && getLinesByProductId(cart, kitProduct.id).length > 0;

  const kitItems: {
    title: string;
    description: string;
    image: ShopifyImage;
  }[] = [
    {
      title: 'Hair collection kit',
      description:
        'The hair collection kit is a white tin box with accessories to collect your hair. You will be asked to collect at least 6 grams of joint hair (and additional 6 grams for every carat weight of diamond you plan to order). Measurement instructions are included as.',
      image: kitProduct.images[3]!,
    },
    {
      title: 'Sample cubic zirconia stones',
      description:
        'The kit will include 3 sample cubic zirconia stones with different carat weights. You can use these to help you decide how large a diamond you plan to order with us.',
      image: kitProduct.images[6]!,
    },
    {
      title: 'Ring sizer',
      description:
        'We provide you a free ring sizer to help you measure your finger size. It is useful to select the right ring size for your Bonded Diamond™ ring.',
      image: kitProduct.images[4]!,
    },
    {
      title: 'Instructions and return label',
      description:
        'Our instructions will guide you through the process of collecting your hair and completing your order. The free return shipping label will be used to return the hair collection kit back to our lab.',
      image: kitProduct.images[5]!,
    },
  ];

  const titleSection = (
    <>
      <h2 className="text-lg uppercase lg:text-xl">{kitProduct.title}</h2>
      <p className="text-xl font-light">
        {renderMoneyRange(kitProduct.priceRange)}
        <span className="text-bd-dark/80 text-sm font-light">
          {' '}
          (Fully refundable at order confirmation)
        </span>
      </p>
    </>
  );
  return (
    <>
      <HSection>
        <BDTitle>Begin your journey with our get started kit</BDTitle>
        <div className="mt-6 flex flex-col justify-between px-4 pb-4 md:hidden">{titleSection}</div>

        <div className="md:mt-6 md:grid md:grid-cols-5 md:items-start">
          {/* Image gallery */}
          <section
            key="image-gallery"
            className="grid grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 md:col-span-2 md:grid-cols-2 md:pr-4"
          >
            {kitProduct.images.map((image, index) => {
              return (
                <BDImage
                  key={image.id}
                  src={image}
                  alt={image.altText || 'product image'}
                  className={`border-bd-contrast h-full w-full cursor-zoom-in rounded-md border ${
                    index % 3 === 0 ? 'md:col-span-2' : 'md:col-span-1'
                  }`}
                  onClick={() => {
                    setSelectedCarouselIndex(index);
                    setGalleryOpen(true);
                  }}
                />
              );
            })}

            <BDDialog
              openButton={null}
              open={galleryOpen}
              onClose={() => setGalleryOpen(false)}
              panelClassName="sm:!max-w-[90dvh]"
            >
              <section className="mt-4">
                <ImageCarousel
                  images={kitProduct.images}
                  selectedItem={selectedCarouselIndex}
                  buttonClassName="text-white"
                />
              </section>
            </BDDialog>
          </section>
          <section
            key="product-details"
            className="mt-8 flex flex-col gap-y-4 overflow-y-auto md:col-span-3 md:mt-0 md:pl-4"
          >
            <section>
              <div className="mb-2 hidden justify-between gap-2 md:flex md:flex-col">
                {titleSection}
              </div>

              <div className="text-bd-dark/80 flex flex-col gap-2">
                <h3 className="sr-only">Description</h3>
                <p className="text-base">
                  The only task to kick off your Bonded Diamond™ journey is to pick up the get
                  started kit. This get started kit includes all the tools and instructions to help
                  you collect and return your hair. It also includes items to help you customize
                  your Bonded Diamond™ and finalize your order later.
                </p>
                <p className="text-base">
                  After adding the get started kit to your cart, you may continue to customize your
                  Bonded Diamond™ and browse our jewelry collections to obtain an instant quote.
                  However, making these selections is optional, and there is no obligation to make
                  such purchases.
                </p>
                <Link href="/learn-more#order-process" className="underline">
                  Learn more about our ordering process.
                </Link>
              </div>

              <form
                className="mt-4 flex flex-row flex-wrap justify-end gap-x-4 gap-y-2"
                action={trackedFormAction}
              >
                <BDButton
                  styleType={kitInCart ? 'secondary' : 'primary'}
                  type="submit"
                  disabled={pending || kitInCart}
                  pending={pending}
                  className="md:w-70 relative flex w-full flex-row items-center justify-center gap-1 px-4 py-2 capitalize"
                >
                  {kitInCart ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      <span>Kit added</span>
                    </>
                  ) : (
                    'Add to cart'
                  )}
                </BDButton>
                {kitInCart && (
                  <>
                    <BDButton
                      href="/shop/diamonds/bonded"
                      styleType="secondary"
                      className="md:w-70 relative flex w-full items-center justify-center gap-1 px-4 py-2 capitalize"
                    >
                      <span>Quote diamonds</span>
                    </BDButton>
                    <BDButton
                      styleType="primary"
                      href="/shop/confirmation"
                      className="md:w-70 relative flex w-full items-center justify-center gap-1 px-4 py-2 capitalize"
                    >
                      Confirm purchase
                    </BDButton>
                  </>
                )}
              </form>
            </section>
            <HorizontalLine />
            <section className="@container pt-6">
              <h2 className="text-xl">What is in the box?</h2>
              <div className="@[40em]:grid-cols-4 mt-6 grid grid-cols-2 gap-4">
                {kitItems.map((item) => (
                  <div key={item.title} className="flex flex-col items-center gap-4">
                    <Image
                      src={item.image.url}
                      alt={item.title}
                      className="aspect-square w-[60%] rounded-full object-cover object-center"
                      width={item.image.width}
                      height={item.image.height}
                    />
                    <h2 className="text-center font-light uppercase">{item.title}</h2>
                    <p className="text-center text-sm font-light">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>
          </section>
        </div>
      </HSection>
    </>
  );
}

const FirstTimeShopService = ({
  cart,
  kitProduct,
  premiumProcessingProduct,
  clubPlatinumVariant,
}: {
  cart: Cart | undefined;
  kitProduct: Product;
  premiumProcessingProduct: Product;
  clubPlatinumVariant: ProductVariant;
}) => {
  const [state, formAction, pending] = useActionState(
    setVariantWithQuantity.bind(null, {
      cartId: cart?.id,
      cartCookieName: CHECKOUT_CART_COOKIE_NAME,
      variantId: kitProduct.variants[0]!.id,
      quantity: 1,
    }),
    null
  );
  const [removeState, removeKitAction, pendingRemoveKit] = useActionState(
    removeProduct.bind(null, {
      cartId: cart?.id!,
      productId: kitProduct.id,
    }),
    null
  );
  const kitInCart = cart && getLinesByProductId(cart, kitProduct.id).length > 0;
  const [premiumState, premiumAction, pendingPremium] = useActionState(
    setVariantWithQuantity.bind(null, {
      cartId: cart?.id,
      cartCookieName: CHECKOUT_CART_COOKIE_NAME,
      variantId: premiumProcessingProduct.variants[0]!.id,
      quantity: 1,
    }),
    null
  );

  const [removePremiumState, removePremiumAction, pendingRemovePremium] = useActionState(
    removeProduct.bind(null, {
      cartId: cart?.id!,
      productId: premiumProcessingProduct.id,
    }),
    null
  );
  const premiumProcessingInCart =
    cart && getLinesByProductId(cart, premiumProcessingProduct.id).length > 0;

  return (
    <div className="relative flex flex-col gap-8">
      <div className="mt-10 px-4 sm:mt-16 sm:px-0 lg:mt-0">
        <h1 className="text-2xl tracking-tight">{kitProduct.title}</h1>

        <div className="mt-3">
          <h2 className="sr-only">Product information</h2>
          <p className="text-xl tracking-tight">{renderMoney(kitProduct.variants[0]!.price)}</p>
        </div>

        {/* {kitInCart ? (
            <form className="mt-6" action={removeKitAction}>
              <div className="mt-10 flex justify-between gap-4">
                <div className="flex items-center gap-2 border-slate-200 p-2 text-sm text-slate-800">
                  <CheckCircleIcon className="h-6 w-6" />
                  <span className="">Kit added to cart</span>
                </div>
                <BDButton
                  styleType="secondary"
                  type="submit"
                  disabled={pending}
                  className="relative flex min-w-32 items-center justify-center gap-1 px-8 py-3"
                >
                  Remove from cart
                  {pendingRemoveKit && <Spinner className="h-4 w-4" />}
                </BDButton>
              </div>
            </form>
          ) : ( */}
      </div>
    </div>
  );
};
